import * as React from 'react';
import { GEO_API } from 'constants/api';
import { GeoResponse } from 'constants/payloads/geo';
import { PARITY_OFFER_MODES, ParityOffer, ParityOfferMode } from 'constants/payloads/parityOffer';
import { isParityOfferDebugEnvironment } from 'utils/shared/parityOffers';

export type GeoLookupStatus = 'loading' | 'success' | 'error';

export interface GeoContextValue {
  status: GeoLookupStatus;
  country: string | null;
  city: string | null;
  region: string | null;
  ip: string | null;
  parity: { provider: ParityOfferMode; offer: ParityOffer | null };
}

interface Props {
  children: React.ReactNode;
}

const DEFAULT_VALUE: GeoContextValue = {
  status: 'loading',
  country: null,
  city: null,
  region: null,
  ip: null,
  parity: { provider: 'custom', offer: null },
};

const GeoContext = React.createContext<GeoContextValue>(DEFAULT_VALUE);

const isParityOffer = (value: unknown): value is ParityOffer => {
  if (!value || typeof value !== 'object') return false;
  const offer = value as Partial<ParityOffer>;
  return (
    typeof offer.countryCode === 'string' &&
    typeof offer.countryName === 'string' &&
    Boolean(offer.countryName) &&
    (offer.tierId === 'regional30' || offer.tierId === 'regional20') &&
    (offer.percentOff === 20 || offer.percentOff === 30) &&
    typeof offer.couponCode === 'string' &&
    offer.duration === 'forever'
  );
};

const nullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isGeoResponse = (value: unknown): value is GeoResponse => {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<GeoResponse>;
  const parity = response.parity;
  return (
    nullableString(response.country) &&
    nullableString(response.city) &&
    nullableString(response.region) &&
    nullableString(response.ip) &&
    !!parity &&
    PARITY_OFFER_MODES.includes(parity.provider as ParityOfferMode) &&
    (parity.offer === null || isParityOffer(parity.offer))
  );
};

const getDebugHeaders = (): HeadersInit | undefined => {
  if (typeof window === 'undefined' || !isParityOfferDebugEnvironment()) return undefined;

  const debugCountry = new URLSearchParams(window.location.search)
    .get('parityCountry')
    ?.trim()
    .toUpperCase();

  return debugCountry && /^[A-Z]{2}$/.test(debugCountry)
    ? { 'x-parity-offer-debug-country': debugCountry }
    : undefined;
};

/**
 * The one geo lookup for the app. Resolves the real visitor (country/city/region/IP) at the
 * Cloudflare edge and the regional parity offer, in a single call per session. Replaces the
 * three separate /api/v1/ip + /api/v1/parity-offer fetches that each read the PoP.
 *
 * Mounted above CurrentUserProvider in _app so CurrentUserContext, UsagePing, and the parity
 * selectors all read from here. Fails to `status: 'error'` with null geo — every consumer
 * degrades safely (usage_ping writes blanks, the banner shows the site-wide sale).
 */
export default function GeoProvider({ children }: Props) {
  const [value, setValue] = React.useState<GeoContextValue>(DEFAULT_VALUE);

  React.useEffect(() => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5000);

    fetch(GEO_API, {
      cache: 'no-store',
      credentials: 'omit',
      headers: getDebugHeaders(),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Geo lookup failed: ${response.status}`);

        const payload: unknown = await response.json();
        if (!isGeoResponse(payload)) throw new Error('Invalid geo response');

        setValue({
          status: 'success',
          country: payload.country,
          city: payload.city,
          region: payload.region,
          ip: payload.ip,
          parity: { provider: payload.parity.provider, offer: payload.parity.offer },
        });
      })
      .catch(() => {
        setValue({ ...DEFAULT_VALUE, status: 'error' });
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, []);

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
}

export const useGeo = (): GeoContextValue => React.useContext(GeoContext);
